// docs/60 DC14: a dungeon crawl's dressing on the board — the lights its chambers are lit by (their
// glow is the light map's, docs/19; this layer draws the thing that gives it) and a mark on each lore
// stone the party has not read yet. Stairs and shrines are fixtures (FixtureLayer), chutes are traps
// in their guise (SecretLayer).
//
// Frames are Oryx's: a wall torch (156) for a sconce, the standing brazier (99), a lit candle on its
// holder (items 145), the blue spotted mushroom (items 95) tinted for glowing moss, an ember bar
// (fx24 57) for a crack in the floor that glows. A shrine's own glow needs no prop.
import Phaser from 'phaser';
import type { CrawlView, FixtureView, TileData } from '../../core/sim/types';
import { atlasForFrame } from '../assets';
import { lightLook } from '../../core/crawl/crawl';

const LOOK_FRAMES: Record<string, string | undefined> = { sconce: 'world_0156', brazier: 'world_0099', candles: 'item_145', moss: 'item_095', shrine: undefined, ember: 'fx24_057' };
const GOLD = 0xffd166, MOSS = 0x7dffc0, EMBER = 0xff7a2a;

export class CrawlLayer {
  private objects: Phaser.GameObjects.GameObject[] = [];

  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly cell: number) {}

  private add<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.layer.add(o);
    this.objects.push(o);
    return o;
  }

  private frame(frame: string, x: number, y: number, scale: number): Phaser.GameObjects.Image | undefined {
    const atlas = atlasForFrame(frame);
    if (!this.scene.textures.exists(atlas) || !this.scene.textures.get(atlas).has(frame)) return undefined;
    const img = this.scene.add.image(x, y, atlas, frame);
    img.setScale((this.cell * scale) / Math.max(1, img.width));
    return this.add(img);
  }

  draw(board: { w: number; tiles: ReadonlyArray<TileData | undefined> }, crawl: CrawlView | undefined, fixtures?: readonly FixtureView[]): void {
    for (const o of this.objects) o.destroy();
    this.objects = [];
    if (!crawl) return;
    const c = this.cell;
    for (const l of crawl.lights) {
      const look = lightLook(l.look).key;
      const x = l.tile.x * c, y = l.tile.y * c, mx = x + c / 2, my = y + c / 2;
      const t = board.tiles[l.tile.y * board.w + l.tile.x];
      const wall = !!t && (t.terrain === 'Wall' || t.terrain === 'Void');
      switch (look) {
        case 'sconce': {
          if (!this.frame(LOOK_FRAMES.sconce!, mx, my, 1)) this.flame(mx, my - 2, 1);
          break;
        }
        case 'brazier': {
          if (!this.frame(LOOK_FRAMES.brazier!, mx, my, 1)) this.flame(mx, my, 1.4);
          break;
        }
        case 'candles': {
          // a cluster of two or three on the floor, or a pair set in a niche of the wall
          const n = 2 + ((l.tile.x * 7 + l.tile.y * 3) % 2);
          for (let i = 0; i < n; i++) {
            const dx = (i - (n - 1) / 2) * c * 0.26, dy = wall ? c * 0.05 : (i % 2 ? c * 0.12 : 0);
            if (!this.frame(LOOK_FRAMES.candles!, mx + dx, my + dy, 0.55)) this.flame(mx + dx, my + dy, 0.5);
          }
          break;
        }
        case 'moss': {
          // a few glowing caps and specks on the stone
          const g = this.add(this.scene.add.graphics());
          for (let i = 0; i < 5; i++) {
            const h = Math.imul(l.tile.x * 73856093 ^ l.tile.y * 19349663, i + 1) >>> 0;
            g.fillStyle(MOSS, 0.55 + ((h >>> 4) % 30) / 100).fillCircle(x + c * (0.2 + ((h >>> 8) % 60) / 100), y + c * (0.3 + ((h >>> 16) % 55) / 100), 0.8 + ((h >>> 12) % 3) * 0.4);
          }
          const cap = this.frame(LOOK_FRAMES.moss!, mx + c * 0.12, my + c * 0.12, 0.5);
          cap?.setTint(0x8affd0);
          break;
        }
        case 'ember': {
          const bar = this.frame(LOOK_FRAMES.ember!, mx, my, 0.9);
          if (bar) bar.setAngle(((l.tile.x * 31 + l.tile.y * 17) % 4) * 45 - 45).setBlendMode(Phaser.BlendModes.ADD);
          else {
            const g = this.add(this.scene.add.graphics());
            g.lineStyle(1.5, EMBER, 0.9).beginPath();
            g.moveTo(x + c * 0.15, y + c * 0.6); g.lineTo(x + c * 0.4, y + c * 0.45); g.lineTo(x + c * 0.6, y + c * 0.62); g.lineTo(x + c * 0.85, y + c * 0.4);
            g.strokePath();
          }
          break;
        }
        default: break;   // a shrine's glow: the shrine draws itself
      }
    }
    // an unread lore stone carries a small gold mark until someone reads it
    const byId = new Map((fixtures ?? []).map((f) => [f.id, f] as const));
    const g = this.add(this.scene.add.graphics());
    for (const l of crawl.lore) {
      if (l.read) continue;
      const f = byId.get(l.fixture);
      if (!f) continue;
      const px = f.tile.x * c + c - 4, py = f.tile.y * c + 4;
      g.fillStyle(0x0b0f1a, 0.85).fillCircle(px, py, 3);
      g.fillStyle(GOLD, 1).fillTriangle(px, py - 2.4, px + 1, py, px - 1, py).fillTriangle(px, py + 2.4, px + 1, py, px - 1, py).fillTriangle(px - 2.4, py, px, py - 1, px, py + 1).fillTriangle(px + 2.4, py, px, py - 1, px, py + 1);
    }
  }

  /** A vector flame for when the atlas lacks the frame. */
  private flame(x: number, y: number, k: number): void {
    const g = this.add(this.scene.add.graphics());
    g.fillStyle(0xff9a3c, 1).fillTriangle(x - 3 * k, y + 2 * k, x + 3 * k, y + 2 * k, x, y - 5 * k);
    g.fillStyle(0xffe08a, 1).fillTriangle(x - 1.5 * k, y + 2 * k, x + 1.5 * k, y + 2 * k, x, y - 2 * k);
  }
}
